import { useEffect, useRef, useState } from "react";
import type { EventSourceMessage } from "@microsoft/fetch-event-source";
import type { AreaFrameUpdateData, AreaFrameUpdateRuntimeStreamEvent } from "@situate/contracts";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";
import {
  advanceLiveTrackHistory,
  type LiveTrackHistorySample,
} from "@/features/deployment-dashboard/live-view/model/live-track-history";

const LIVE_TRACKS_STREAM_PATH = "/v1/live/streams/tracks";
const HEARTBEAT_TIMEOUT_MS = 60_000;
const MAX_RETRY_DELAY_MS = 30_000;
const BASE_RETRY_DELAY_MS = 1_000;

export type LiveStreamStatus =
  "disabled" | "connecting" | "open" | "retrying" | "offline" | "auth-failed" | "error";

interface LiveTracksStreamState {
  latestAreaFrame: AreaFrameUpdateData | null;
  trackHistory: LiveTrackHistorySample[];
  tick: number;
  status: LiveStreamStatus;
}

interface DeploymentFrameState {
  deploymentId: string | null;
  areaId: string | null;
  frame: AreaFrameUpdateData | null;
  trackHistory: LiveTrackHistorySample[];
  tick: number;
}

function buildLiveTracksStreamPath(areaId: string): string {
  const params = new URLSearchParams({ area_ids: areaId });
  return `${LIVE_TRACKS_STREAM_PATH}?${params.toString()}`;
}

/**
 * Subscribes to the runtime API's live tracks SSE stream for one selected area.
 * The trusted runtime event is parsed once before the view derives markers,
 * trails, population, and AOI counts from object IDs and x/y positions.
 */
export function useLiveTracksStream(
  deploymentId: string,
  areaId: string | null,
  enabled = true,
  trackTtlTicks = 1
): LiveTracksStreamState {
  const runtime = useRuntimeHttpClient();
  const [state, setState] = useState<DeploymentFrameState>({
    deploymentId,
    areaId,
    frame: null,
    trackHistory: [],
    tick: 0,
  });
  const [status, setStatus] = useState<LiveStreamStatus>("disabled");
  const [isVisible, setIsVisible] = useState(() => document.visibilityState === "visible");
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const generationRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const lastEventAtRef = useRef(Date.now());
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdogIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryAttemptRef = useRef(0);

  function getRetryDelay(attempt: number): number {
    const exponentialDelay = Math.min(MAX_RETRY_DELAY_MS, BASE_RETRY_DELAY_MS * 2 ** attempt);
    return exponentialDelay * (0.8 + Math.random() * 0.4);
  }

  function clearTimers() {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (watchdogIntervalRef.current) {
      clearInterval(watchdogIntervalRef.current);
      watchdogIntervalRef.current = null;
    }
  }

  function resetRetryState() {
    retryAttemptRef.current = 0;
  }

  useEffect(() => {
    setState((previous) => {
      if (previous.deploymentId === deploymentId && previous.areaId === areaId) return previous;
      return { deploymentId, areaId, frame: null, trackHistory: [], tick: 0 };
    });
  }, [deploymentId, areaId]);

  useEffect(() => {
    function handleVisibilityChange() {
      setIsVisible(document.visibilityState === "visible");
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
    }
    function handleOffline() {
      setIsOnline(false);
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const hasLiveTracksGrant = runtime.operationGrants.some(
      (grant) => grant.operation_id === "live_tracks_stream"
    );
    const shouldStream =
      enabled &&
      areaId !== null &&
      isVisible &&
      isOnline &&
      runtime.client !== null &&
      runtime.deploymentId === deploymentId &&
      hasLiveTracksGrant;

    if (!shouldStream) {
      controllerRef.current?.abort();
      controllerRef.current = null;
      clearTimers();
      if (!enabled || areaId === null || !isVisible || !runtime.client || !hasLiveTracksGrant) {
        setStatus("disabled");
      } else if (!isOnline) {
        setStatus("offline");
      }
      return;
    }

    const client = runtime.client;
    if (!client) return;
    const selectedAreaId = areaId;

    function startStream() {
      if (client === null) return;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      controllerRef.current?.abort();

      const generation = ++generationRef.current;
      const controller = new AbortController();
      controllerRef.current = controller;
      lastEventAtRef.current = Date.now();
      setStatus("connecting");

      function onMessage(message: EventSourceMessage) {
        if (generation !== generationRef.current || controller.signal.aborted) return;
        lastEventAtRef.current = Date.now();

        if (message.event === "heartbeat") {
          setStatus("open");
          resetRetryState();
          return;
        }
        if (message.event !== "area_frame_update" || !message.data) return;

        try {
          const event = JSON.parse(message.data) as AreaFrameUpdateRuntimeStreamEvent;
          const frame = event.data;
          if (frame.area_id !== selectedAreaId) return;

          setStatus("open");
          resetRetryState();
          setState((previous) => {
            if (previous.deploymentId !== deploymentId || previous.areaId !== selectedAreaId) {
              return previous;
            }

            const tick = previous.tick + 1;
            return {
              deploymentId,
              areaId: selectedAreaId,
              frame,
              trackHistory: advanceLiveTrackHistory(
                previous.trackHistory,
                tick,
                frame,
                Math.max(1, Math.floor(trackTtlTicks))
              ),
              tick,
            };
          });
        } catch {
          console.error("[live-tracks-stream] failed to parse an incoming message");
        }
      }

      function onError(error: unknown) {
        if (generation !== generationRef.current || controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === "AbortError") return;

        let shouldRetry = true;
        const retryDelay = getRetryDelay(retryAttemptRef.current);
        if (error instanceof Error) {
          const message = error.message.toLowerCase();
          if (message.includes("403") || message.includes("forbidden")) {
            console.error("[live-tracks-stream] access denied (403), not retrying");
            setStatus("auth-failed");
            shouldRetry = false;
          } else if (message.includes("401") || message.includes("unauthorized")) {
            shouldRetry = false;
            if (runtime.refreshSession) {
              console.warn("[live-tracks-stream] unauthorized (401), refreshing runtime session");
              setStatus("retrying");
              void runtime.refreshSession().catch((refreshError: unknown) => {
                if (generation === generationRef.current) {
                  console.error(
                    "[live-tracks-stream] failed to refresh runtime session",
                    refreshError
                  );
                  setStatus("error");
                }
              });
            } else {
              console.error("[live-tracks-stream] unauthorized (401), refresh unavailable");
              setStatus("auth-failed");
            }
          } else {
            console.warn(
              `[live-tracks-stream] transient error, will retry in ${Math.round(retryDelay)}ms:`,
              error
            );
            setStatus("retrying");
            retryAttemptRef.current += 1;
          }
        }

        if (shouldRetry) {
          retryTimeoutRef.current = setTimeout(() => {
            if (generation === generationRef.current) startStream();
          }, retryDelay);
        }
      }

      client
        .subscribe(buildLiveTracksStreamPath(selectedAreaId), { onMessage }, controller.signal)
        .catch(onError);
    }

    startStream();
    watchdogIntervalRef.current = setInterval(() => {
      if (Date.now() - lastEventAtRef.current > HEARTBEAT_TIMEOUT_MS) {
        console.warn(
          `[live-tracks-stream] no events for ${Math.round((Date.now() - lastEventAtRef.current) / 1000)}s, restarting stream`
        );
        startStream();
      }
    }, HEARTBEAT_TIMEOUT_MS / 2);

    return () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
      clearTimers();
    };
  }, [areaId, deploymentId, enabled, isOnline, isVisible, runtime, trackTtlTicks]);

  const hasCurrentScope = state.deploymentId === deploymentId && state.areaId === areaId;
  const latestAreaFrame = hasCurrentScope ? state.frame : null;

  return {
    latestAreaFrame,
    trackHistory: hasCurrentScope ? state.trackHistory : [],
    tick: hasCurrentScope ? state.tick : 0,
    status,
  };
}
